import React, { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingCart, User, LogOut, ShieldCheck, Menu, X, ShoppingBag, Users as TeamIcon, Wallet, ArrowDownToLine, UserCircle, Store, MessageCircle, MapPin } from "lucide-react";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/catalog", label: "Katalog" },
  { to: "/orders", label: "Pesanan", auth: true },
  { to: "/team", label: "Tim", auth: true },
  { to: "/topup", label: "Top Up", auth: true },
  { to: "/seller", label: "Jadi Penjual" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [drawer, setDrawer] = useState(false);
  useEffect(() => { setOpen(false); setDrawer(false); }, [loc.pathname]);
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-50 bg-[#FF6A00] text-white border-b border-black" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-logo">
            <div className="w-8 h-8 bg-black text-[#FF6A00] grid place-items-center font-mono font-black text-lg">A</div>
            <span className="font-display text-xl font-black tracking-tight uppercase">AleShop<span className="text-black">Now</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 flex-1 justify-center">
            {navItems.filter(n => !n.auth || user).map(n => (
              <Link key={n.to} to={n.to} className="label-mono text-white hover:text-black transition-colors">{n.label}</Link>
            ))}
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/cart" className="relative p-2 border border-black bg-white text-black hover:bg-black hover:text-white transition-colors" data-testid="cart-button">
              <ShoppingCart className="w-4 h-4" />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#FF3333] text-white text-[10px] font-mono font-bold w-5 h-5 grid place-items-center border border-black" data-testid="cart-count">{count}</span>
              )}
            </Link>
            {user ? (
              <>
                <div className="hidden lg:flex flex-col text-right leading-tight">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/80">Saldo</span>
                  <span className="font-mono text-sm font-bold text-white" data-testid="user-balance">${Number(user.balance).toFixed(2)}</span>
                </div>
                <button onClick={() => setDrawer(true)} className="flex items-center gap-2 px-3 py-2 border border-black bg-white text-black hover:bg-black hover:text-white" data-testid="profile-toggle">
                  <UserCircle className="w-4 h-4" />
                  <span className="hidden sm:inline font-mono text-xs font-bold uppercase max-w-[100px] truncate">{user.name}</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline-flex bg-black text-white px-5 py-2.5 font-mono uppercase text-xs font-bold tracking-widest border border-black hover:bg-white hover:text-black transition-colors" data-testid="login-link">
                  <User className="w-4 h-4 mr-2 inline" /> Masuk
                </Link>
                <Link to="/register" className="hidden sm:inline-flex bg-white text-black px-5 py-2.5 font-mono uppercase text-xs font-bold tracking-widest border border-black hover:bg-black hover:text-white transition-colors">Daftar</Link>
              </>
            )}
            <button onClick={() => setOpen(!open)} className="md:hidden p-2 border border-black bg-white text-black" data-testid="mobile-menu">
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-black bg-white text-black px-4 py-4 flex flex-col gap-3">
            {navItems.filter(n => !n.auth || user).map(n => (
              <Link key={n.to} to={n.to} className="label-mono hover:text-[#FF6A00]">{n.label}</Link>
            ))}
            {!user && (
              <>
                <Link to="/login" className="btn-primary text-center">Masuk</Link>
                <Link to="/register" className="btn-secondary text-center">Daftar</Link>
              </>
            )}
          </div>
        )}
      </header>
      {user && drawer && (
        <ProfileDrawer onClose={() => setDrawer(false)} user={user} logout={async () => { await logout(); nav("/"); setDrawer(false); }} />
      )}
      <main className="flex-1"><Outlet /></main>
      <footer className="border-t border-black bg-[#FF6A00] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-2">
            <div className="font-display text-3xl font-black uppercase tracking-tight">AleShop<span className="text-black">Now</span></div>
            <p className="font-mono text-xs uppercase tracking-widest text-white/80 mt-2">// aleshopnow.store</p>
            <p className="text-sm text-white/90 mt-4 max-w-md">Platform jual beli untuk penjual lokal dengan pasar yang lebih luas.</p>
          </div>
          <div>
            <div className="label-mono text-white/80 mb-3">Produk</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/catalog?type=digital" className="hover:text-black">Digital</Link></li>
              <li><Link to="/catalog?type=physical" className="hover:text-black">Fisik</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-mono text-white/80 mb-3">Akun</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/profile" className="hover:text-black">Profil</Link></li>
              <li><Link to="/addresses" className="hover:text-black">Alamat Saya</Link></li>
              <li><Link to="/seller" className="hover:text-black">Jadi Penjual</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-black px-4 md:px-8 py-4 font-mono text-[10px] uppercase tracking-widest text-white/80 flex justify-between bg-black">
          <span>© 2026 AleShopNow</span>
          <span>STATUS: ONLINE</span>
        </div>
      </footer>
      <a href="https://t.me/COMMISSIONER1105" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[999] flex items-center gap-2 bg-black text-white px-4 py-3 border border-black font-mono text-xs font-bold uppercase tracking-widest hover:bg-[#FF6A00] transition-colors shadow-brutal">
        <MessageCircle className="w-4 h-4" />
        Layanan Pelanggan
      </a>
    </div>
  );
}

function ProfileDrawer({ onClose, user, logout }) {
  const items = [
    { to: "/profile", icon: UserCircle, label: "Profil & Akun" },
    { to: "/orders", icon: ShoppingBag, label: "Pesanan Saya" },
    { to: "/team", icon: TeamIcon, label: "Tim & Referral" },
    { to: "/topup", icon: Wallet, label: "Isi Saldo" },
    { to: "/withdraw", icon: ArrowDownToLine, label: "Tarik Dana" },
    { to: "/addresses", icon: MapPin, label: "Alamat Saya" },
    { to: "/seller", icon: Store, label: "Mode Penjual" },
  ];
  return (
    <div className="fixed inset-0 z-[60] flex" onClick={onClose}>
      <div className="bg-black/60 flex-1" />
      <aside onClick={(e) => e.stopPropagation()} className="w-80 max-w-[85vw] bg-white border-l border-black flex flex-col" data-testid="profile-drawer">
        <div className="bg-[#FF6A00] text-white p-5 border-b border-black">
          <div className="flex justify-between items-start">
            <div>
              <div className="label-mono">// PROFIL</div>
              <div className="font-display text-2xl font-black uppercase tracking-tight mt-1 break-all">{user.name}</div>
              <div className="font-mono text-xs mt-1 opacity-90">{user.phone}</div>
            </div>
            <button onClick={onClose} className="p-1 border border-black bg-white text-black hover:bg-black hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="mt-4 border border-black bg-black text-white p-3">
            <div className="label-mono text-[#FFD600]">// SALDO</div>
            <div className="font-mono text-2xl font-black mt-1">${Number(user.balance).toFixed(2)}</div>
            <div className="flex gap-2 mt-3">
              <Link to="/topup" onClick={onClose} className="flex-1 text-center bg-[#FFD600] text-black px-3 py-1.5 border border-white font-mono text-[10px] font-bold uppercase">+ Isi</Link>
              <Link to="/withdraw" onClick={onClose} className="flex-1 text-center bg-white text-black px-3 py-1.5 border border-white font-mono text-[10px] font-bold uppercase">Tarik</Link>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {user.role === "admin" && (
            <Link to="/admin" onClick={onClose} className="flex items-center gap-3 px-3 py-3 border border-black bg-[#FFD600] font-mono text-xs uppercase font-bold tracking-widest hover:bg-black hover:text-[#FFD600]" data-testid="drawer-admin">
              <ShieldCheck className="w-4 h-4" /> Panel Admin
            </Link>
          )}
          {items.map(it => (
            <Link key={it.to} to={it.to} onClick={onClose} className="flex items-center gap-3 px-3 py-3 border border-transparent hover:border-black hover:bg-zinc-50 font-mono text-xs uppercase font-bold tracking-widest">
              <it.icon className="w-4 h-4" /> {it.label}
            </Link>
          ))}
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-3 border border-black hover:bg-[#FF3333] hover:text-white font-mono text-xs uppercase font-bold tracking-widest mt-4">
            <LogOut className="w-4 h-4" /> Keluar
          </button>
        </nav>
        <div className="p-3 border-t border-black font-mono text-[10px] text-zinc-500">
          // KODE REFERRAL: <b className="text-black">{user.referral_code}</b>
        </div>
      </aside>
    </div>
  );
}